import React, { useMemo } from 'react';
import { usePaginationFragment } from 'react-relay/hooks';

import { ListSubheader } from '@material-ui/core';
import { graphql } from 'babel-plugin-relay/macro';
import { EmptyStateList, InfiniteScrollV2 } from 'embeds/components';
import { compact, get, isEmpty, map, reject, size } from 'lodash';

import { ListChatActivityAssignees_EmbedQuery$key } from './__generated__/ListChatActivityAssignees_EmbedQuery.graphql';
import ListItemAssignee from './ListItemAssignee';
import ListNodes, { ListNodesProps } from './ListNodes';

export interface ListChatActivityAssigneesProps extends ListNodesProps {
  fragmentRef: ListChatActivityAssignees_EmbedQuery$key;
  includeSubheaders?: boolean;
  includeMe?: boolean;
  includeAvatar?: boolean;
  includeEmail?: boolean;
  includeChatActivityCount?: boolean;
}

const ListChatActivityAssignees: React.FC<ListChatActivityAssigneesProps> = ({
  fragmentRef,
  includeSubheaders,
  includeMe: initialIncludeMe,
  includeAvatar,
  includeEmail,
  includeChatActivityCount,
  ...rest
}) => {
  const {
    data: { viewer, userMany, groupMany, providerConnection },
    loadNext,
    isLoadingNext,
    hasNext,
  } = usePaginationFragment<any, ListChatActivityAssignees_EmbedQuery$key>(
    graphql`
      fragment ListChatActivityAssignees_EmbedQuery on Query
        @argumentDefinitions(
          cursor: { type: "String" }
          count: { type: "Int", defaultValue: 15 }
          regex: { type: "String" }
          includeAvatar: { type: "Boolean", defaultValue: false }
          includeEmail: { type: "Boolean", defaultValue: false }
          includeChatActivityCount: { type: "Boolean", defaultValue: false }
          filterChatActivityCount: { type: "FilterChatActivityInput", defaultValue: null }
        )
        @refetchable(queryName: "ListChatActivityAssignees_Embed_refetchable") {
        viewer {
          _id
          ...ListItemAssignee_EmbedNode
            @arguments(
              includeAvatar: $includeAvatar
              includeEmail: $includeEmail
              includeChatActivityCount: $includeChatActivityCount
              filterChatActivityCount: $filterChatActivityCount
            )
        }
        userMany(
          sort: NAME_ASC
          filter: { type: staff, active: 1, _operators: { name: { regex: $regex }, roles: { ne: "widget" } } }
        ) {
          _id
          ...ListItemAssignee_EmbedNode
            @arguments(
              includeAvatar: $includeAvatar
              includeEmail: $includeEmail
              includeChatActivityCount: $includeChatActivityCount
              filterChatActivityCount: $filterChatActivityCount
            )
        }
        groupMany(sort: NAME_ASC, filter: { _operators: { name: { regex: $regex }, type: { ne: system } } }) {
          _id
          ...ListItemAssignee_EmbedNode
            @arguments(
              includeAvatar: $includeAvatar
              includeChatActivityCount: $includeChatActivityCount
              filterChatActivityCount: $filterChatActivityCount
            )
        }
        providerConnection(
          first: $count
          sort: NAME_ASC
          after: $cursor
          filter: { active: 1, isRenderingProvider: true, visible: true, _operators: { name: { regex: $regex } } }
        ) @connection(key: "ListChatActivityAssignees_providerConnection") {
          edges {
            node {
              _id
              ...ListItemAssignee_EmbedNode
                @arguments(
                  includeAvatar: $includeAvatar
                  includeEmail: $includeEmail
                  includeChatActivityCount: $includeChatActivityCount
                  filterChatActivityCount: $filterChatActivityCount
                )
            }
          }
        }
      }
    `,
    fragmentRef,
  );
  const groups = compact(groupMany);
  const allUsers = compact(userMany);
  const users = reject(allUsers, { _id: get(viewer, '_id') });
  const allProviders = compact(map(get(providerConnection, 'edges'), 'node'));
  const providers = reject(compact(allProviders), { _id: get(viewer, '_id') });

  // Move `me` from grouped items to the first position
  const includeMe = useMemo(
    () => initialIncludeMe || size(allUsers) !== size(users) || size(allProviders) !== size(providers),
    [initialIncludeMe, allUsers, users, allProviders, providers],
  );

  return (
    <InfiniteScrollV2
      hasNext={hasNext}
      isLoadingNext={isLoadingNext}
      // WORKAROUND fetch 100 until sorting by custom fields is solved (this will replace the initial 15)
      // https://app.clickup.com/t/210gzn
      loadNext={() => loadNext(100)}
    >
      <ListNodes {...rest}>
        {isEmpty(groups) && isEmpty(users) && isEmpty(providers) && !includeMe ? (
          <EmptyStateList />
        ) : (
          <>
            {viewer && includeMe && (
              <ListItemAssignee
                key={viewer._id}
                fragmentRef={viewer}
                includeAvatar={includeAvatar}
                includeEmail={includeEmail}
                includeChatActivityCount={includeChatActivityCount}
                me
              />
            )}
            {includeSubheaders && !isEmpty(users) && <ListSubheader>Staff</ListSubheader>}
            {map(users, (user) => (
              <ListItemAssignee
                key={user._id}
                fragmentRef={user}
                includeAvatar={includeAvatar}
                includeEmail={includeEmail}
                includeChatActivityCount={includeChatActivityCount}
              />
            ))}
            {includeSubheaders && !isEmpty(groups) && <ListSubheader>Groups</ListSubheader>}
            {map(groups, (group) => (
              <ListItemAssignee
                key={group._id}
                fragmentRef={group}
                includeAvatar={includeAvatar}
                includeEmail={includeEmail}
                includeChatActivityCount={includeChatActivityCount}
              />
            ))}
            {includeSubheaders && !isEmpty(providers) && <ListSubheader>Providers</ListSubheader>}
            {map(providers, (provider) => (
              <ListItemAssignee
                key={provider._id}
                fragmentRef={provider}
                includeAvatar={includeAvatar}
                includeEmail={includeEmail}
                includeChatActivityCount={includeChatActivityCount}
              />
            ))}
          </>
        )}
      </ListNodes>
    </InfiniteScrollV2>
  );
};

export default ListChatActivityAssignees;
